/**
 * Client-side avatar preparation (§14.79): any picked image becomes a 256 px square
 * (centre cover-crop) encoded as WebP, or JPEG where the browser cannot encode WebP.
 * The server refuses anything over 200 KB or not JPEG/PNG/WebP; this stays far under both.
 */

export const AVATAR_SIZE = 256;
/** Keep in sync with AVATAR_MAX_BYTES in convex/functions/lib/avatars.ts. */
export const AVATAR_MAX_BYTES = 200 * 1024;
const QUALITY = 0.85;

/** The largest centred square of a width × height image: the source rect for a cover-crop. */
export function coverCrop(width: number, height: number): { sx: number; sy: number; side: number } {
  const side = Math.min(width, height);
  return { sx: (width - side) / 2, sy: (height - side) / 2, side };
}

function toBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, QUALITY));
}

/**
 * Decodes an image file and returns it as an `AVATAR_SIZE` square WebP (JPEG where WebP
 * encoding is unsupported, e.g. older Safari). Rejects when the file is not a decodable image.
 */
export async function resizeAvatar(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = AVATAR_SIZE;
    canvas.height = AVATAR_SIZE;
    const ctx2d = canvas.getContext('2d');
    if (!ctx2d) throw new Error('Canvas 2D is not available');
    const { sx, sy, side } = coverCrop(bitmap.width, bitmap.height);
    ctx2d.drawImage(bitmap, sx, sy, side, side, 0, 0, AVATAR_SIZE, AVATAR_SIZE);
    const webp = await toBlob(canvas, 'image/webp');
    if (webp && webp.type === 'image/webp') return webp;
    const jpeg = await toBlob(canvas, 'image/jpeg');
    if (jpeg) return jpeg;
    throw new Error('Could not encode the image');
  } finally {
    bitmap.close();
  }
}
